import { ReactNode } from "react";
import { KeyValueObject, TailwindColors } from "@components/common/types/general";

type ColorVariableColorKeys = keyof TailwindColors | "colorize" | "default";

export function getColorVariable(color: string | undefined, theme: any, colors: Partial<Record<ColorVariableColorKeys, string>>): string {
    const colorKey: ColorVariableColorKeys = colors[color as ColorVariableColorKeys]
        ? (color as ColorVariableColorKeys)
        : color === undefined
        ? "default"
        : "colorize";
    if (colors[colorKey]) {
        return colors[colorKey]!;
    } else {
        if (colorKey === "colorize") {
            return theme.colors[color || "primary"][500];
        }
        return theme.colors.white;
    }
}

export function createShouldForwardPropBlockList(blockList: PropertyKey[]): (prop: PropertyKey) => boolean {
    return function (prop: PropertyKey): boolean {
        return !blockList.includes(prop);
    };
}

export function objectToCss(obj: any): string {
    let cssString = "";
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            cssString += obj[key];
        }
    }
    return cssString;
}

export function isExternalLink(url: string): boolean {
    const domain = process.env.NEXT_PUBLIC_FRONTEND_URL || "http://localhost:3000";
    const urlDomain = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/?\n]+)/i)?.[1];
    return urlDomain === domain;
}

export function remToPx(rem: string): number {
    return parseInt(rem.replace("rem", ""), 10) * 16;
}

export function isReactText(children: ReactNode): boolean {
    return ["string", "number"].includes(typeof children);
}

export function deepMerge(...objects: Array<Record<string, any> | null | undefined>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const obj of objects) {
        if (obj && typeof obj === "object") {
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    if (typeof obj[key] === "object" && !Array.isArray(obj[key]) && obj[key] !== null) {
                        result[key] = deepMerge(result[key] || {}, obj[key]);
                    } else {
                        result[key] = obj[key];
                    }
                }
            }
        }
    }
    return result;
}

export function convertCssArrayToObject(cssArray: string[]): KeyValueObject {
    return cssArray.reduce((accumulator, cssRule) => {
        const [property, value] = cssRule.split(":").map((s) => s.trim());
        if (property && value) {
            accumulator[property] = value.replace(/;$/, "");
        }
        return accumulator;
    }, {} as KeyValueObject);
}

export function fixedContent(open: boolean, ariaHidden: string, siteElement?: HTMLElement, bodyElement?: HTMLElement, fixedClass?: string) {
    const site = siteElement || (document.getElementsByClassName("site")[0] as HTMLElement);
    if (site) {
        const body = bodyElement || document.body;
        const currentScrollTop = window.scrollY;
        site.setAttribute("aria-hidden", ariaHidden);
        if (open && !body.classList.contains(fixedClass || "site-fixed")) {
            body.classList.add(fixedClass || "site-fixed");
            site.style.marginTop = currentScrollTop * -1 + "px";
            body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        } else if (!open && body.classList.contains(fixedClass || "site-fixed")) {
            const newScrollTop = parseInt(site.style.marginTop);
            site.removeAttribute("style");
            body.classList.remove(fixedClass || "site-fixed");
            body.scrollTop = newScrollTop * -1;
            document.documentElement.scrollTop = newScrollTop * -1;
        }
    }
}

export function pluck<T, K extends keyof T>(items: T[], key: K): T[K][] {
    return items.map((item) => item[key]);
}

export function hexToRGBA(hexCode: string, opacity: number) {
    var hex = hexCode.replace("#", "");
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    var r = parseInt(hex.substring(0, 2), 16),
        g = parseInt(hex.substring(2, 4), 16),
        b = parseInt(hex.substring(4, 6), 16);
    return "rgba(" + r + ", " + g + ", " + b + ", " + opacity / 100 + ")";
}

export function resolveCssVarToHex(cssVarName: string): string {
    const el = document.documentElement;
    const computed = getComputedStyle(el).getPropertyValue(cssVarName).trim();
    return computed || "";
}
